import { useMutation, useQuery } from "@apollo/client";
import { Form, Formik } from "formik";
import gql from "graphql-tag";
import * as Cookies from "js-cookie";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useHistory, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import * as Yup from "yup";
import { toastErrorOptions, toastSuccessOptions } from "../../../App";
import getCurrentUserInformation from "../../../GraphQL/Query/getCurrentUserInformation";
import { Button } from "../../SharedComponents/Button/Button";
import { DeleteButton } from "../../SharedComponents/DeleteButton/DeleteButton";
import { DeleteModalContent } from "../../SharedComponents/DeleteModalContent/DeleteModalContent";
import {
  EditableInformation,
  EditableInformationCheckboxInput,
  EditableInformationItemWrapper,
  EditableInformationTextInput,
  EditableInformationTextSelect,
} from "../../SharedComponents/EditableInformation/EditableInformation";
import { EditableInformationCalendar } from "../../SharedComponents/EditableInformation/EditableInformationCalendar/EditableInformationCalendar";
import { EditHeader } from "../../SharedComponents/EditHeader/EditHeader";
import { Modal } from "../../SharedComponents/Modal/Modal";
import {
  AdminUsersControlList,
  getUsers,
} from "../AdminUsersControlList/AdminUsersControlList";
import "./AdminDistributorEdit.css";

export const AdminDistributorEdit = () => {
  const { t } = useTranslation();
  const [canDelete, setCanDelete] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const accessLevel = Cookies.get("access_level");
  /** @type {*} */
  const params = useParams();
  const history = useHistory();

  const { data, error, loading } = useQuery(getDistributor, {
    variables: { id: params.id },
  });

  const { data: currentUserData } = useQuery(getCurrentUserInformation);

  const { data: countryData } = useQuery(countriesQuery);

  const { data: childrenData } = useQuery(getDistributorChildrenQuery, {
    variables: { id: params.id },
    pollInterval: 10000,
  });

  const {
    data: usersData,
    error: usersError,
    loading: usersLoading,
  } = useQuery(getUsers, {
    variables: { accountIds: [params.id] },
    fetchPolicy: "network-only",
  });

  const [updateAccount] = useMutation(updateDistributorMutation);

  const [deleteAccount] = useMutation(deleteSSOAccount);

  useEffect(() => {
    if (data?.distributor?.information) {
      const children = childrenData?.childAccounts?.length;
      const usersOnAccount = usersData?.users?.length;

      if (children === 0 && usersOnAccount === 0) {
        setCanDelete(true);
      }
    }
  }, [data, childrenData, usersData]);

  if (loading || error) {
    return <div>Loading...</div>;
  }

  const canEdit =
    accessLevel === "SUPERADMIN" ||
    currentUserData?.user?.roles?.sso?.find((x) => x.accountId === params.id)
      ?.role !== "SUPPORTER";

  return (
    <div className="AdminDistributorEdit">
      {showDeleteModal && (
        <Modal
          acceptText={t("distributor.delete")}
          content={
            <DeleteModalContent
              text={t("distributor.deleteModalBody", {
                distributorname: data?.distributor?.name,
              })}
            />
          }
          title={t("distributor.delete")}
          accept={() => {
            deleteAccount({ variables: { id: data?.distributor?.id } })
              .then(() => {
                setShowDeleteModal(false);
                history.goBack();
              })
              .catch((err) => console.error(err));
          }}
          reject={() => {
            setShowDeleteModal(false);
          }}
          acceptButtonColor="#AA2525"
        />
      )}
      <Formik
        initialValues={{
          name: data?.distributor?.name,
          email: data?.distributor?.email,
          vat: data?.distributor?.vat,
          address: data?.distributor?.address,
          postalCode: data?.distributor?.postalCode,
          city: data?.distributor?.city,
          country: data?.distributor?.country,
          clientId: data?.distributor?.clientId,
          status: data?.distributor?.status,
          minimumLicenses: data?.distributor?.minimumLicenses,
          canWorkflow: data?.distributor?.allowedApps?.includes("WORKFLOW"),
          canInventory: data?.distributor?.allowedApps?.includes("INVENTORY"),
          startDate: new Date(data?.distributor?.startDate),
          contactName: data?.distributor?.contactName,
          contactEmail: data?.distributor?.contactEmail,
          contactPhone: data?.distributor?.contactPhone,
        }}
        onSubmit={(updatedValues) => {
          const hasWorkflow = updatedValues.canWorkflow ? "WORKFLOW" : "";
          const hasInventory = updatedValues.canInventory ? "INVENTORY" : "";
          const allowedApps = `${hasWorkflow},${hasInventory}`;
          updateAccount({
            variables: {
              id: params.id,
              ...updatedValues,
              allowedApps,
            },
          })
            .then(() => {
              toast(t("toasts.distributorUpdated"), toastSuccessOptions);
            })
            .catch((err) => {
              toast(t(err.message), toastErrorOptions);
            });
        }}
        validationSchema={validationSchema}
      >
        {({ isValid, dirty }) => (
          <Form style={{ display: "flex", width: "100%", overflow: "hidden" }}>
            <div className="AdminDistributorEdit__content">
              <EditHeader
                type="types.distributor"
                name={data?.distributor?.name || " "}
                status={
                  data?.distributor?.suspendedAt
                    ? "states.suspended"
                    : "states.active"
                }
                goBack={() => history.goBack()}
              />
              <div className="AdminDistributorEdit__companyinfo">
                <EditableInformation title="sectionTitles.companyInformations">
                  <EditableInformationItemWrapper label="accounts.name">
                    {<EditableInformationTextInput name="name" type="text" />}
                  </EditableInformationItemWrapper>
                  <EditableInformationItemWrapper label="generic.email">
                    <EditableInformationTextInput name="email" type="text" />
                  </EditableInformationItemWrapper>
                  <EditableInformationItemWrapper label="accounts.status">
                    <EditableInformationTextSelect
                      name="status"
                      options={selectOptionsForAccountStatus}
                      type="text"
                    />
                  </EditableInformationItemWrapper>
                  <EditableInformationItemWrapper label="accounts.vat">
                    <EditableInformationTextInput name="vat" type="text" />
                  </EditableInformationItemWrapper>
                  <EditableInformationItemWrapper label="accounts.address">
                    <EditableInformationTextInput name="address" type="text" />
                  </EditableInformationItemWrapper>
                  <EditableInformationItemWrapper label="accounts.postalCode">
                    <EditableInformationTextInput
                      name="postalCode"
                      type="text"
                    />
                  </EditableInformationItemWrapper>
                  <EditableInformationItemWrapper label="accounts.city">
                    <EditableInformationTextInput name="city" type="text" />
                  </EditableInformationItemWrapper>
                  <EditableInformationItemWrapper label="accounts.country">
                    <EditableInformationTextSelect
                      name="country"
                      type="text"
                      canEdit={canEdit}
                      options={
                        countryData?.countries?.map((country) => ({
                          label: country.name,
                          value: country.name,
                        })) || []
                      }
                      withSquare={false}
                    />
                  </EditableInformationItemWrapper>
                  <EditableInformationItemWrapper label="accounts.clientId">
                    <EditableInformationTextInput name="clientId" type="text" />
                  </EditableInformationItemWrapper>
                  <EditableInformationItemWrapper label="accounts.contactPerson">
                    <EditableInformationTextInput
                      name="contactName"
                      type="text"
                    />
                  </EditableInformationItemWrapper>
                  <EditableInformationItemWrapper label="accounts.contactEmail">
                    <EditableInformationTextInput
                      name="contactEmail"
                      type="text"
                    />
                  </EditableInformationItemWrapper>
                  <EditableInformationItemWrapper label="accounts.contactPhone">
                    <EditableInformationTextInput
                      name="contactPhone"
                      type="text"
                    />
                  </EditableInformationItemWrapper>
                  <EditableInformationItemWrapper label="accounts.licensesMin">
                    <EditableInformationTextInput
                      name="minimumLicenses"
                      type="text"
                      canEdit={canEdit}
                    />
                  </EditableInformationItemWrapper>
                  <EditableInformationItemWrapper label="accounts.startDate">
                    <EditableInformationCalendar
                      name="startDate"
                      type="text"
                      canEdit={canEdit}
                    />
                  </EditableInformationItemWrapper>
                  <EditableInformationItemWrapper label="accounts.allowedApps">
                    <div className="AdminDistributorEdit__content--apps">
                      <EditableInformationCheckboxInput
                        name="canWorkflow"
                        type="checkbox"
                        label="Workflow"
                        defaultEditingState={true}
                      />
                      <EditableInformationCheckboxInput
                        name="canInventory"
                        type="checkbox"
                        label="Inventory"
                        defaultEditingState={true}
                      />
                    </div>
                  </EditableInformationItemWrapper>
                </EditableInformation>
              </div>

              {/* Add component that shows administrative users */}
              <div className="AdminDistributorEdit__administrativeusers">
                <AdminUsersControlList
                  accountId={params.id}
                  selectOptions={options}
                  canManage={canEdit}
                  data={usersData}
                  error={usersError}
                  loading={usersLoading}
                />
              </div>
              {canEdit && (
                <div className="AdminDistributorEdit__delete-account--wrapper">
                  <div className="AdminDistributorEdit__delete-account">
                    <DeleteButton
                      label={t("accounts.delete")}
                      onClick={(e) => {
                        e.preventDefault();
                        setShowDeleteModal(true);
                      }}
                      maxWidth="148px"
                      disabled={!canDelete}
                    />
                    <p className="AdminDistributorEdit__delete-account-text">
                      {t("accounts.transferBeforeDelete")}
                    </p>
                  </div>
                </div>
              )}
            </div>
            <div className="AdminDistributorEdit__right">
              <div className="AdminDistributorEdit__right--actions">
                <Button
                  buttonStyle="Button__primary"
                  disabled={!isValid || !dirty}
                  type="submit"
                >
                  {t("generic.save")}
                </Button>
                <Button
                  buttonStyle="Button__secondary"
                  click={(e) => {
                    e.preventDefault();
                    history.goBack();
                  }}
                >
                  {t("generic.cancel")}
                </Button>
              </div>
            </div>
          </Form>
        )}
      </Formik>
    </div>
  );
};

const options = [
  { label: "roles.admin", value: "ADMIN" },
  { label: "roles.supporter", value: "SUPPORTER" },
  { label: "roles.employee", value: "EMPLOYEE" },
];

const selectOptionsForAccountStatus = [
  {
    label: "states.suspended",
    value: "Suspended",
  },
  {
    label: "states.active",
    value: "Active",
  },
];

const getDistributor = gql`
  query distributor($id: String) {
    distributor(id: $id) {
      id
      name
      email
      status
      information
      clientId
      postalCode
      address
      vat
      country
      minimumLicenses
      allowedApps
      startDate
      contactName
      contactEmail
      contactPhone
    }
  }
`;

const getDistributorChildrenQuery = gql`
  query childAccounts($id: String) {
    childAccounts(id: $id) {
      id
    }
  }
`;

const updateDistributorMutation = gql`
  mutation updateSSOAccount(
    $id: ID!
    $name: String
    $email: String
    $status: String
    $clientId: String
    $address: String
    $postalCode: String
    $country: String
    $vat: String
    $minimumLicenses: Int
    $allowedApps: String
    $startDate: String
    $contactName: String
    $contactEmail: String
    $contactPhone: String
  ) {
    updateSSOAccount(
      id: $id
      name: $name
      email: $email
      status: $status
      clientId: $clientId
      address: $address
      postalCode: $postalCode
      country: $country
      vat: $vat
      minimumLicenses: $minimumLicenses
      allowedApps: $allowedApps
      startDate: $startDate
      contactName: $contactName
      contactEmail: $contactEmail
      contactPhone: $contactPhone
    ) {
      id
      clientId
      name
      email
      vat
      address
      postalCode
      country
      createdAt
      updatedAt
      newToken
      role
      parentId
      suspendedAt
      status
      information
      minimumLicenses
      allowedApps
      startDate
      contactName
      contactEmail
      contactPhone
    }
  }
`;

const countriesQuery = gql`
  query {
    countries {
      id
      name
      code
    }
  }
`;

const deleteSSOAccount = gql`
  mutation removeSSOAccount($id: String) {
    removeSSOAccount(id: $id) {
      id
      message
    }
  }
`;

const validationSchema = Yup.object().shape({
  name: Yup.string().required("You must enter a name"),
  email: Yup.string().email().required("You must enter an e-mail"),
  vat: Yup.string().required("You must enter a VAT code"),
  address: Yup.string().required("You must enter an address"),
  postalCode: Yup.string().required("You must enter a postal code"),
  country: Yup.string().required("You must enter a country"),
  clientId: Yup.string().required("You must enter a client id"),
  status: Yup.string().required(),
});
